import { useEffect, useRef } from 'react';
import { HudChip } from './HudChip';

/**
 * Floating position-slot picker — surfaces after a drag commits to a
 * zone different from the token's previous role. The coach explicitly
 * confirms (or changes) what slot the dragged player now occupies.
 *
 * Distinct from `RoleInstructionsPicker` which handles role
 * VARIATIONS within a slot (CB → stopper / ball-playing / sweeper).
 * This picker handles the slot itself — the position label that
 * shows on the token + drives the formation projection.
 *
 * Slots are grouped by line (defence / defensive mid / central mid /
 * attacking mid / attack). Auto-inferred suggestion is highlighted +
 * appears first.
 */

const SLOT_GROUPS: Array<{ label: string; slots: string[] }> = [
  { label: 'Defence', slots: ['GK', 'LB', 'CB', 'RB', 'LWB', 'RWB'] },
  { label: 'Defensive mid', slots: ['LDM', 'CDM', 'RDM'] },
  { label: 'Central mid', slots: ['LCM', 'CM', 'RCM'] },
  { label: 'Wide mid', slots: ['LM', 'RM'] },
  { label: 'Attacking mid', slots: ['LAM', 'CAM', 'RAM'] },
  { label: 'Attack', slots: ['LW', 'LF', 'ST', 'RF', 'RW'] },
];

export interface PositionSlotPickerProps {
  /** Current role tag the spot has (pre-drag). */
  currentSlot: string;
  /** Suggested slot for the new position — drives the highlighted
   *  "Suggested" affordance. */
  suggested: string;
  /** Coach picks a slot — caller commits to the spot. */
  onPick: (slot: string) => void;
  /** Dismiss without changing role (Esc / click-outside / Keep). */
  onClose: () => void;
}

export function PositionSlotPicker({
  currentSlot,
  suggested,
  onPick,
  onClose,
}: PositionSlotPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  // Only show slots in the area the token was dragged to — the line
  // containing the suggested slot, FURTHER narrowed to the same
  // side so a coach who drags an RB to a slightly different
  // right-defensive position doesn't get LB / GK as options.
  //
  // Side rules:
  //   - GK is its own slot — only show it if the suggested IS GK.
  //   - A suggestion with an L prefix shows L-prefix slots + centre
  //     slots (no R-prefix opponents).
  //   - R prefix mirrors.
  //   - Centre / no-prefix suggestion shows the full line (no info to
  //     filter on).
  const suggestedGroup = SLOT_GROUPS.find((g) => g.slots.includes(suggested));
  const sideOf = (slot: string): 'L' | 'R' | 'C' => {
    if (slot.startsWith('L')) return 'L';
    if (slot.startsWith('R')) return 'R';
    return 'C';
  };
  const suggestedSide = sideOf(suggested);
  const visibleGroups = (suggestedGroup ? [suggestedGroup] : SLOT_GROUPS).map(
    (group) => ({
      ...group,
      slots: group.slots.filter((slot) => {
        // GK is its own concept — only show when suggested is GK.
        if (slot === 'GK') return suggested === 'GK';
        if (suggestedSide === 'C') return true;
        const side = sideOf(slot);
        return side === suggestedSide || side === 'C';
      }),
    }),
  );

  // Click-outside + Escape — both dismiss the picker without commit.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    const onDocPointer = (e: PointerEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (e.target instanceof Node && root.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    // Defer one tick — otherwise the same pointerup that triggered the
    // picker would immediately fall through here as "click outside."
    const t = setTimeout(() => {
      window.addEventListener('pointerdown', onDocPointer);
    }, 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDocPointer);
      clearTimeout(t);
    };
  }, [onClose]);

  return (
    <div
      ref={rootRef}
      className="thud__slot-picker"
      role="dialog"
      aria-label={`Pick position for token (currently ${currentSlot})`}
      data-testid="thud-slot-picker"
    >
      <div className="thud__slot-picker-header">
        <span className="thud__slot-picker-title">Pick position</span>
        <span className="thud__slot-picker-suggestion">
          Suggested: <strong>{suggested}</strong>
        </span>
      </div>
      {visibleGroups.map((group) => (
        <div
          key={group.label}
          className="thud__slot-picker-row"
          role="group"
          aria-label={group.label}
        >
          <span className="thud__slot-picker-row-label">{group.label}</span>
          <div className="thud__slot-picker-row-chips">
            {group.slots.map((slot) => (
              <HudChip
                key={slot}
                active={slot === suggested}
                ariaLabel={`Set position to ${slot}${
                  slot === suggested ? ' (suggested)' : ''
                }`}
                onClick={() => onPick(slot)}
              >
                {slot}
              </HudChip>
            ))}
          </div>
        </div>
      ))}
      <div className="thud__slot-picker-footer">
        <HudChip
          active={false}
          ariaLabel={`Keep the existing ${currentSlot} role — close picker without changing`}
          onClick={onClose}
        >
          Keep as {currentSlot}
        </HudChip>
      </div>
    </div>
  );
}
